/* Oreganix product components for Dawn. No external dependencies or network calls
   other than Shopify's own /cart/add.js on this store. */

(() => {
  const formatMoney = (cents, format) => {
    const value = Number(cents) || 0;
    const fmt = format || '${{amount}}';
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (number / 100).toFixed(precision).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0);
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'", '.');
        default:
          return withDelimiters(value, 2);
      }
    });
  };

  const onVariantChange = (sectionId, callback) => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return () => {};
    return subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event?.data?.sectionId === sectionId && event.data.variant) callback(event.data.variant);
    });
  };

  /* ---------- Product form hooks ----------
     Blocks register extra cart lines (upsells, gifts) and validators (required fields)
     on a product form. One capture listener on the <product-form> wrapper runs them
     before Dawn's own submit handler. */
  const formHooks = new WeakMap();
  const getFormHooks = (form) => {
    if (!form) return null;
    if (formHooks.has(form)) return formHooks.get(form);
    const hooks = { extras: new Set(), validators: new Set() };
    formHooks.set(form, hooks);
    const host = form.parentElement;
    host?.addEventListener(
      'submit',
      async (event) => {
        if (event.target !== form || form.dataset.oxExtrasAdded === 'true') return;
        // Run every validator so each invalid field shows its message.
        const results = [...hooks.validators].map((validate) => validate());
        if (results.includes(false)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          document.querySelector('[data-ox-invalid]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        const items = [...hooks.extras].flatMap((collect) => collect() || []);
        if (!items.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        try {
          await fetch(`${window.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
        } catch (error) {
          console.error(error);
        }
        form.dataset.oxExtrasAdded = 'true';
        form.requestSubmit();
        delete form.dataset.oxExtrasAdded;
      },
      true
    );
    return hooks;
  };

  const formQuantity = (form) => {
    if (!form) return 1;
    return Math.max(parseInt(new FormData(form).get('quantity'), 10) || 1, 1);
  };

  // Adds lines directly (bundle button) and refreshes Dawn's cart drawer or notification.
  const addToCart = async (items) => {
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { items };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }
    const response = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const state = await response.json();
    if (state.status) throw new Error(state.description || state.message);
    if (!cart) {
      window.location = window.routes.cart_url;
      return state;
    }
    if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ox-bundle', cartData: state });
    cart.classList.remove('is-empty');
    // cart-notification reads the first added line from `key`/`id`.
    cart.renderContents({ ...state, ...(state.items?.[0] || {}), sections: state.sections });
    return state;
  };

  /* ---------- Quantity breaks ---------- */
  if (!customElements.get('ox-quantity-breaks')) {
    customElements.define(
      'ox-quantity-breaks',
      class OxQuantityBreaks extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section;
          this.form = document.getElementById(this.dataset.form);
          this.moneyFormat = this.dataset.moneyFormat;
          this.price = Number(this.dataset.variantPrice);
          this.compare = Number(this.dataset.variantCompare);
          this.options = [...this.querySelectorAll('.ox-qb__option')];
          this.hiddenQty = this.querySelector('[data-qb-quantity]');

          this.addEventListener('change', (event) => {
            if (event.target.classList.contains('ox-qb__radio')) this.select(event.target.closest('.ox-qb__option'));
            if (event.target.matches('[data-qb-item]')) {
              const picked = event.target.selectedOptions[0];
              const thumb = event.target.closest('.ox-qb__item')?.querySelector('[data-qb-thumb]');
              if (thumb && picked?.dataset.thumb) {
                thumb.src = picked.dataset.thumb;
                thumb.removeAttribute('srcset');
              }
              this.showMedia(picked?.dataset.media);
            }
            if (event.target.classList.contains('ox-qb__upsell-input')) event.stopPropagation();
          });
          // Clicking an upsell checkbox must not re-trigger the option label.
          this.addEventListener('click', (event) => {
            if (event.target.closest('.ox-qb__upsell-input')) event.stopPropagation();
          });

          this.unsubscribe = onVariantChange(this.sectionId, (variant) => {
            this.price = variant.price;
            this.compare = Math.max(variant.compare_at_price || 0, variant.price);
            this.render();
          });

          this.interceptSubmit();
          const initial = this.options.find((o) => o.classList.contains('is-selected')) || null;
          if (initial) this.select(initial);
          this.render();
        }

        disconnectedCallback() {
          this.unsubscribe?.();
          if (this.form) getFormHooks(this.form).extras.delete(this.collect);
        }

        get selected() {
          return this.options.find((o) => o.classList.contains('is-selected'));
        }

        select(option) {
          this.options.forEach((o) => {
            const active = o === option;
            o.classList.toggle('is-selected', active);
            const radio = o.querySelector('.ox-qb__radio');
            if (radio) radio.checked = active;
          });
          this.applyQuantity();
        }

        // Shows the picked variant's image in the product gallery.
        showMedia(mediaId) {
          if (!mediaId) return;
          document.getElementById(`MediaGallery-${this.sectionId}`)?.setActiveMedia(`${this.sectionId}-${mediaId}`, true, true);
        }

        // Variant ids chosen per unit in the selected option, or [] when per-item pickers are off.
        itemIds() {
          if (!this.selected) return [];
          return [...this.selected.querySelectorAll('[data-qb-item]')].map((select) => Number(select.value));
        }

        applyQuantity() {
          // With per-item pickers the form submits only the first unit; the rest go in as extra lines.
          const qty = this.itemIds().length ? 1 : this.selected ? Number(this.selected.dataset.qty) : 1;
          const themeInput = document.getElementById(`Quantity-${this.sectionId}`);
          if (themeInput) {
            themeInput.value = qty;
            themeInput.dispatchEvent(new Event('change', { bubbles: true }));
            this.hiddenQty.disabled = true;
          } else if (this.selected) {
            this.hiddenQty.value = qty;
            this.hiddenQty.disabled = false;
          }
        }

        compute(option) {
          const qty = Number(option.dataset.qty);
          const pct = Number(option.dataset.pct) || 0;
          const fixed = Number(option.dataset.fixed) || 0;
          const line = this.price * qty;
          // Same rounding as the Liquid render and Shopify discounts: floor the percentage discount, then subtract.
          const finalPrice = Math.max(line - Math.floor((line * pct) / 100) - fixed, 0);
          const compare = option.dataset.compareMode === 'compare_price' && this.compare > this.price ? this.compare * qty : line;
          const saved = Math.max(compare - finalPrice, 0);
          return {
            qty,
            finalPrice,
            compare,
            saved,
            pct: compare > 0 ? Math.round((saved * 100) / compare) : 0,
          };
        }

        fill(template, values) {
          return (template || '')
            .replaceAll('[quantity]', values.qty)
            .replaceAll('[price]', formatMoney(values.finalPrice, this.moneyFormat))
            .replaceAll('[compare_price]', formatMoney(values.compare, this.moneyFormat))
            .replaceAll('[amount_saved]', formatMoney(values.saved, this.moneyFormat))
            .replaceAll('[percentage]', `${values.pct}%`);
        }

        render() {
          this.options.forEach((option) => {
            const values = this.compute(option);
            option.querySelectorAll('[data-template]').forEach((el) => {
              el.innerHTML = this.fill(el.dataset.template, values);
              if (el.classList.contains('ox-qb__compare')) el.hidden = values.compare <= values.finalPrice;
              if (el.classList.contains('ox-qb__caption') && el.dataset.template.includes('[amount_saved]')) el.hidden = values.saved === 0;
            });
          });
          if (this.dataset.updatePdp === 'true' && this.selected) {
            const values = this.compute(this.selected);
            const priceEl = document.querySelector(`#price-${this.sectionId} .ox-price__current, #price-${this.sectionId} .price-item--last`);
            if (priceEl) priceEl.textContent = formatMoney(values.finalPrice, this.moneyFormat);
          }
        }

        upsellIds() {
          if (!this.selected) return [];
          return [...this.selected.querySelectorAll('[data-upsell]')]
            .filter((u) => u.dataset.type === 'gift' || u.querySelector('.ox-qb__upsell-input')?.checked)
            .map((u) => Number(u.dataset.variantId));
        }

        interceptSubmit() {
          this.collect = () => {
            const upsells = this.upsellIds().map((id) => ({ id, quantity: 1 }));
            const [first, ...rest] = this.itemIds();
            if (!first) return upsells;
            const idInput = this.form?.querySelector('[name="id"]');
            if (idInput) {
              idInput.disabled = false;
              idInput.value = first;
            }
            const counts = new Map();
            rest.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
            return [...[...counts].map(([id, quantity]) => ({ id, quantity })), ...upsells];
          };
          getFormHooks(this.form)?.extras.add(this.collect);
        }
      }
    );
  }

  /* ---------- Shipping checkpoints ---------- */
  if (!customElements.get('ox-shipping-checkpoints')) {
    customElements.define(
      'ox-shipping-checkpoints',
      class OxShippingCheckpoints extends HTMLElement {
        connectedCallback() {
          const split = (value) =>
            (value || '')
              .split(',')
              .map((v) => v.trim())
              .filter(Boolean);
          this.days = split(this.dataset.days);
          this.months = split(this.dataset.months);
          // Weekday names to skip, matched against the configured day labels (Mon..Sun).
          this.skipDays = split(this.dataset.skipDays).map((d) => d.toLowerCase());
          // Dates to skip as MM/DD.
          this.skipDates = split(this.dataset.skipDates);

          this.querySelectorAll('.ox-ship__top').forEach((el) => {
            const start = this.addBusinessDays(Number(el.dataset.min) || 0);
            const end = this.addBusinessDays(Number(el.dataset.max) || 0);
            el.innerHTML = el.dataset.template
              .replaceAll('[start_date]', this.format(start))
              .replaceAll('[end_date]', this.format(end));
          });
        }

        dayLabel(date) {
          // Labels are configured Monday-first; JS getDay() is Sunday-first.
          return this.days[(date.getDay() + 6) % 7] || '';
        }

        isSkipped(date) {
          const label = this.dayLabel(date).toLowerCase();
          const md = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
          return this.skipDays.includes(label) || this.skipDates.includes(md);
        }

        addBusinessDays(count) {
          const date = new Date();
          let added = 0;
          let guard = 0;
          while (added < count && guard < 366) {
            date.setDate(date.getDate() + 1);
            guard += 1;
            if (!this.isSkipped(date)) added += 1;
          }
          return date;
        }

        format(date) {
          const day = this.dayLabel(date);
          const month = this.months[date.getMonth()] || date.getMonth() + 1;
          const dd = date.getDate();
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          switch (this.dataset.format) {
            case 'day_mm_dd':
              return `${day}, ${month} ${dd}`;
            case 'day_dd_mm':
              return `${day}, ${dd}. ${month}`;
            case 'dd_mm':
              return `${dd}. ${month}`;
            case 'dd_mm_no_dot':
              return `${dd} ${month}`;
            case 'day_dd_mm_numeric':
              return `${day}, ${String(dd).padStart(2, '0')}. ${mm}.`;
            case 'dd_mm_numeric':
              return `${String(dd).padStart(2, '0')}. ${mm}.`;
            default:
              return `${month} ${dd}`;
          }
        }
      }
    );
  }

  /* ---------- Sticky add to cart ---------- */
  if (!customElements.get('ox-sticky-atc')) {
    customElements.define(
      'ox-sticky-atc',
      class OxStickyAtc extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section;
          this.moneyFormat = this.dataset.moneyFormat;
          this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.form = document.getElementById(`product-form-${this.sectionId}`);

          this.querySelectorAll('[data-sticky-button]').forEach((button) =>
            button.addEventListener('click', () => this.activate())
          );
          this.querySelectorAll('[data-sticky-select]').forEach((select) =>
            select.addEventListener('change', () => this.selectVariant(select.selectedOptions[0]))
          );

          if (this.dataset.display === 'after_scroll') {
            const anchor = this.mainButton || document.getElementById(`MainProduct-${this.sectionId}`);
            if (anchor) {
              // Checked on scroll: an observer misses jumps that skip straight past the button.
              let queued = false;
              this.onScroll = () => {
                if (queued) return;
                queued = true;
                requestAnimationFrame(() => {
                  queued = false;
                  this.classList.toggle('is-visible', anchor.getBoundingClientRect().bottom < 0);
                });
              };
              window.addEventListener('scroll', this.onScroll, { passive: true });
              this.onScroll();
            } else {
              this.classList.add('is-visible');
            }
          }

          this.unsubscribe = onVariantChange(this.sectionId, (variant) => {
            this.querySelectorAll('[data-sticky-price]').forEach((el) => (el.textContent = formatMoney(variant.price, this.moneyFormat)));
            this.querySelectorAll('[data-sticky-compare]').forEach((el) => {
              el.textContent = formatMoney(variant.compare_at_price, this.moneyFormat);
              el.hidden = !(variant.compare_at_price > variant.price);
            });
            this.querySelectorAll('[data-sticky-select]').forEach((select) => (select.value = String(variant.id)));
            this.querySelectorAll('[data-sticky-button]').forEach((button) => (button.disabled = !variant.available));
          });
        }

        disconnectedCallback() {
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
          this.unsubscribe?.();
        }

        activate() {
          const fn = this.dataset.function || 'add_to_cart';
          if (fn === 'add_to_cart' && this.form) {
            this.form.requestSubmit(this.mainButton || undefined);
            return;
          }
          const selector = fn.replace('scroll_', '');
          const map = {
            '#ProductInfo-id': `#ProductInfo-${this.sectionId}`,
            '#variant-selects-id': `variant-selects`,
            '#quantity-breaks-id': `ox-quantity-breaks`,
          };
          const target = document.querySelector(map[selector] || selector);
          target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        selectVariant(option) {
          if (!option) return;
          const values = (option.dataset.options || '').split('||');
          const picker = document.querySelector(`#MainProduct-${this.sectionId} variant-selects, variant-selects`);
          if (!picker) return;
          values.forEach((value, index) => {
            const fieldset = picker.querySelectorAll('fieldset, .product-form__input--dropdown')[index];
            if (!fieldset) return;
            const select = fieldset.querySelector('select');
            if (select) {
              const match = [...select.options].find((o) => o.value === value || o.textContent.trim() === value);
              if (match && select.value !== match.value) {
                select.value = match.value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
              }
              return;
            }
            const radio = [...fieldset.querySelectorAll('input[type="radio"]')].find((r) => r.value === value);
            if (radio && !radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      }
    );
  }

  /* ---------- Generic slider (slide / loop / fade) ---------- */
  if (!customElements.get('ox-slider')) {
    customElements.define(
      'ox-slider',
      class OxSlider extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-track]');
          this.slides = [...this.querySelectorAll('[data-slide]')];
          if (this.slides.length < 2) {
            // A lone fade slide is hidden until marked active.
            this.slides[0]?.classList.add('is-active');
            return;
          }
          this.index = 0;
          this.type = this.dataset.type || 'slide';
          this.dots = this.querySelector('[data-dots]');
          if (this.dots) {
            this.dots.innerHTML = this.slides
              .map((_, i) => `<button type="button" class="ox-slider__dot" aria-label="${i + 1}" data-dot="${i}"></button>`)
              .join('');
            this.dots.addEventListener('click', (e) => {
              const dot = e.target.closest('[data-dot]');
              if (dot) this.navigate(Number(dot.dataset.dot));
            });
          }
          this.querySelector('[data-prev]')?.addEventListener('click', () => this.navigate(this.index - 1));
          this.querySelector('[data-next]')?.addEventListener('click', () => this.navigate(this.index + 1));

          let startX = null;
          this.track.addEventListener('pointerdown', (e) => (startX = e.clientX));
          this.track.addEventListener('pointerup', (e) => {
            if (startX === null) return;
            const dx = e.clientX - startX;
            if (Math.abs(dx) > 40) this.navigate(this.index + (dx < 0 ? 1 : -1));
            startX = null;
          });

          // Autoplay pauses only while hovered or focused, then resumes.
          this.autoplay = this.dataset.autoplay === 'true';
          this.speed = (Number(this.dataset.speed) || 5) * 1000;
          if (this.autoplay) {
            this.addEventListener('mouseenter', () => this.stop());
            this.addEventListener('mouseleave', () => this.start());
            this.addEventListener('focusin', () => this.stop());
            this.addEventListener('focusout', () => this.start());
            this.start();
          }
          this.go(0);
        }

        disconnectedCallback() {
          this.stop();
        }

        // Manual navigation restarts the autoplay countdown so the chosen slide stays up for a full interval.
        navigate(target) {
          this.go(target);
          if (this.timer) this.start();
        }

        start() {
          this.stop();
          if (this.autoplay) this.timer = setInterval(() => this.go(this.index + 1), this.speed);
        }

        stop() {
          clearInterval(this.timer);
          this.timer = null;
        }

        go(target) {
          const last = this.slides.length - 1;
          let next = target;
          if (next > last) next = this.type === 'slide' && !this.autoplay ? last : 0;
          if (next < 0) next = this.type === 'slide' ? 0 : last;
          this.index = next;
          if (this.type === 'fade') {
            this.slides.forEach((s, i) => s.classList.toggle('is-active', i === next));
          } else {
            this.track.style.transform = `translateX(-${next * 100}%)`;
          }
          this.dots?.querySelectorAll('[data-dot]').forEach((d, i) => d.classList.toggle('is-active', i === next));
        }
      }
    );
  }

  /* ---------- Price inside the add-to-cart button ---------- */
  if (!customElements.get('ox-buy-buttons')) {
    customElements.define(
      'ox-buy-buttons',
      class OxBuyButtons extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section;
          this.button = this.querySelector('button[name="add"]');
          if (!this.button) return;
          const icon = this.querySelector('[data-icon-template]');
          if (icon && !this.button.querySelector('.ox-buy__icon')) {
            const span = document.createElement('span');
            span.className = 'ox-buy__icon';
            span.append(icon.content.cloneNode(true));
            this.button.prepend(span);
          }
          if (this.dataset.showPrice === 'true') {
            this.priceEl = document.createElement('span');
            this.priceEl.className = 'ox-buy__price';
            this.button.append(this.priceEl);
            this.render(Number(this.dataset.price), Number(this.dataset.compare));
          }
          if (this.dataset.subtext && !this.button.querySelector('.ox-buy__sub')) {
            const sub = document.createElement('span');
            sub.className = 'ox-buy__sub';
            sub.textContent = this.dataset.subtext;
            this.button.append(sub);
          }
          this.unsubscribe = onVariantChange(this.sectionId, (variant) => this.render(variant.price, variant.compare_at_price || 0));
        }

        disconnectedCallback() {
          this.unsubscribe?.();
        }

        render(price, compare) {
          if (!this.priceEl) return;
          const format = this.dataset.moneyFormat;
          const compareHtml =
            this.dataset.showCompare === 'true' && compare > price ? ` <s>${formatMoney(compare, format)}</s>` : '';
          this.priceEl.innerHTML = `<span class="ox-buy__sep">·</span> ${formatMoney(price, format)}${compareHtml}`;
        }
      }
    );
  }

  /* ---------- Countdown timer ---------- */
  if (!customElements.get('ox-countdown')) {
    customElements.define(
      'ox-countdown',
      class OxCountdown extends HTMLElement {
        connectedCallback() {
          this.units = [...this.querySelectorAll('[data-unit]')];
          this.end = this.resolveEnd();
          this.tick();
          this.timer = setInterval(() => this.tick(), 1000);
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        resolveEnd() {
          if (this.dataset.mode === 'fixed') {
            const parsed = Date.parse(this.dataset.end.trim().replace(' ', 'T'));
            if (!Number.isNaN(parsed)) return parsed;
          }
          // Evergreen: restarts per visitor after it runs out.
          const key = `oxCountdown:${this.dataset.key}`;
          const minutes = Number(this.dataset.minutes) || 15;
          let end = 0;
          try {
            end = Number(localStorage.getItem(key)) || 0;
          } catch (e) {}
          if (!end || end < Date.now()) {
            end = Date.now() + minutes * 60000;
            try {
              localStorage.setItem(key, String(end));
            } catch (e) {}
          }
          return end;
        }

        tick() {
          let left = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
          if (left === 0 && this.dataset.mode === 'fixed') {
            clearInterval(this.timer);
            if (this.dataset.expired) {
              this.querySelector('[data-units]').textContent = this.dataset.expired;
              return;
            }
          }
          if (left === 0) this.end = this.resolveEnd();
          const parts = [Math.floor(left / 86400), Math.floor((left % 86400) / 3600), Math.floor((left % 3600) / 60), left % 60];
          this.units.forEach((el, i) => (el.textContent = String(parts[i]).padStart(2, '0')));
        }
      }
    );
  }

  const splitList = (value) =>
    (value || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

  /* ---------- Estimated delivery ---------- */
  if (!customElements.get('ox-delivery-date')) {
    customElements.define(
      'ox-delivery-date',
      class OxDeliveryDate extends HTMLElement {
        connectedCallback() {
          this.skipDays = splitList(this.dataset.skip).map(Number);
          this.holidays = splitList(this.dataset.holidays);
          this.cutoff = Number(this.dataset.cutoff);
          this.textEl = this.querySelector('[data-text]');
          this.render();
          if (this.dataset.template.includes('[time_left]')) this.timer = setInterval(() => this.render(), 60000);
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        isOff(date) {
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          const dd = String(date.getDate()).padStart(2, '0');
          return (
            this.skipDays.includes(date.getDay()) ||
            this.holidays.includes(`${mm}/${dd}`) ||
            this.holidays.includes(`${date.getFullYear()}-${mm}-${dd}`)
          );
        }

        addBusinessDays(from, count) {
          const date = new Date(from);
          let added = 0;
          for (let guard = 0; added < count && guard < 400; guard += 1) {
            date.setDate(date.getDate() + 1);
            if (!this.isOff(date)) added += 1;
          }
          return date;
        }

        // Next moment orders stop shipping the same business day.
        nextCutoff(now) {
          const date = new Date(now);
          date.setHours(this.cutoff, 0, 0, 0);
          for (let guard = 0; (date <= now || this.isOff(date)) && guard < 400; guard += 1) {
            date.setDate(date.getDate() + 1);
          }
          return date;
        }

        format(date) {
          const locale = this.dataset.locale || undefined;
          const options = {
            weekday_short: { weekday: 'short', month: 'short', day: 'numeric' },
            weekday_long: { weekday: 'long', month: 'long', day: 'numeric' },
            month_day: { month: 'short', day: 'numeric' },
            numeric: { month: '2-digit', day: '2-digit' },
          };
          try {
            if (this.dataset.format === 'day_month') {
              const parts = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).formatToParts(date);
              const part = (type) => parts.find((p) => p.type === type)?.value || '';
              return `${part('day')} ${part('month')}`;
            }
            return new Intl.DateTimeFormat(locale, options[this.dataset.format] || options.weekday_short).format(date);
          } catch (error) {
            return date.toDateString();
          }
        }

        render() {
          const now = new Date();
          const today = new Date(now);
          today.setHours(0, 0, 0, 0);
          const hasCutoff = this.cutoff < 24;
          // Past the cut-off, or on a day off, the order starts processing one business day later.
          const late = this.isOff(today) || (hasCutoff && now.getHours() >= this.cutoff);
          const extra = late ? 1 : 0;
          const min = Number(this.dataset.min) || 0;
          const max = Math.max(Number(this.dataset.max) || 0, min);
          const start = this.addBusinessDays(today, min + extra);
          const end = this.addBusinessDays(today, max + extra);

          let timeLeft = '';
          if (hasCutoff) {
            const minutes = Math.max(Math.ceil((this.nextCutoff(now) - now) / 60000), 1);
            const hours = Math.floor(minutes / 60);
            timeLeft = hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
          }
          this.textEl.innerHTML = this.dataset.template
            .replaceAll('[start_date]', this.format(start))
            .replaceAll('[end_date]', this.format(end))
            .replaceAll('[time_left]', timeLeft);
        }
      }
    );
  }

  /* ---------- Custom product fields ---------- */
  const updateFieldLock = (form) => {
    if (!form) return;
    const fields = [...document.querySelectorAll(`ox-custom-field[data-required][data-form="${form.getAttribute('id')}"]`)];
    // Fields not upgraded yet are still empty at page load.
    const locked = fields.some((field) => (typeof field.isFilled === 'function' ? !field.isFilled() : true));
    form.closest('product-info, [id^="MainProduct-"]')?.toggleAttribute('data-ox-locked', locked);
    form.querySelectorAll('[type="submit"]').forEach((button) => button.toggleAttribute('data-ox-locked', locked));
  };

  if (!customElements.get('ox-custom-field')) {
    customElements.define(
      'ox-custom-field',
      class OxCustomField extends HTMLElement {
        connectedCallback() {
          this.form = document.getElementById(this.dataset.form);
          this.error = this.querySelector('[data-error]');
          const onInput = () => {
            if (this.isFilled()) this.setInvalid(false);
            this.querySelectorAll('.ox-field__choice').forEach((label) => {
              label.classList.toggle('is-selected', !!document.getElementById(label.htmlFor)?.checked);
            });
            updateFieldLock(this.form);
          };
          this.addEventListener('input', onInput);
          this.addEventListener('change', onInput);
          if (this.hasAttribute('data-required')) {
            this.validate = () => {
              const ok = this.isFilled();
              this.setInvalid(!ok);
              return ok;
            };
            getFormHooks(this.form)?.validators.add(this.validate);
          }
          updateFieldLock(this.form);
        }

        disconnectedCallback() {
          if (this.form && this.validate) getFormHooks(this.form).validators.delete(this.validate);
        }

        isFilled() {
          if (this.dataset.type === 'checkbox') return !!this.querySelector('input[type="checkbox"]')?.checked;
          if (this.dataset.type === 'swatch' || this.dataset.type === 'pills') return !!this.querySelector('input:checked');
          return (this.querySelector('.ox-field__input')?.value || '').trim() !== '';
        }

        setInvalid(invalid) {
          this.classList.toggle('is-invalid', invalid);
          this.toggleAttribute('data-ox-invalid', invalid);
          if (this.error) this.error.hidden = !invalid;
        }
      }
    );
  }

  /* ---------- Modal dialogs (size chart) ---------- */
  if (!window.oxDialogsBound) {
    window.oxDialogsBound = true;
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-ox-dialog-open]');
      if (opener) {
        const dialog = document.getElementById(opener.dataset.oxDialogOpen);
        if (dialog?.showModal) {
          dialog.showModal();
          document.documentElement.classList.add('ox-dialog-open');
          dialog.addEventListener('close', () => document.documentElement.classList.remove('ox-dialog-open'), { once: true });
        }
        return;
      }
      if (event.target.closest('[data-ox-dialog-close]')) {
        event.target.closest('dialog')?.close();
        return;
      }
      // A click on the dialog element itself is a click on its backdrop.
      if (event.target.matches?.('dialog.ox-dialog')) event.target.close();
    });
  }

  /* ---------- Gifts on quantity ---------- */
  if (!customElements.get('ox-gifts')) {
    customElements.define(
      'ox-gifts',
      class OxGifts extends HTMLElement {
        connectedCallback() {
          this.form = document.getElementById(this.dataset.form);
          this.items = [...this.querySelectorAll('[data-gift]')];
          this.bar = this.querySelector('[data-bar]');
          this.variantTitle = (this.dataset.variantTitle || '').toLowerCase();
          this.onChange = () => requestAnimationFrame(() => this.update());
          document.addEventListener('change', this.onChange);
          this.unsubscribe = onVariantChange(this.dataset.section, (variant) => {
            this.variantTitle = (variant.title || '').toLowerCase();
            this.update();
          });
          if (this.dataset.autoAdd === 'true') {
            this.collect = () =>
              this.items
                .filter((item) => item.classList.contains('is-unlocked') && item.dataset.variantId)
                .map((item) => ({ id: Number(item.dataset.variantId), quantity: 1, properties: { _ox_gift: 'Free gift' } }));
            getFormHooks(this.form)?.extras.add(this.collect);
          }
          this.update();
        }

        disconnectedCallback() {
          document.removeEventListener('change', this.onChange);
          this.unsubscribe?.();
          if (this.form && this.collect) getFormHooks(this.form).extras.delete(this.collect);
        }

        update() {
          const qty = formQuantity(this.form);
          this.items.forEach((item) => {
            let unlocked;
            if (this.dataset.mode === 'variant') {
              const names = splitList(item.dataset.variants);
              unlocked = names.some((name) => this.variantTitle === name || this.variantTitle.split(' / ').includes(name));
            } else {
              unlocked = qty >= (Number(item.dataset.min) || 1);
            }
            item.classList.toggle('is-unlocked', unlocked);
            const status = item.querySelector('.ox-gifts__status');
            if (status) status.textContent = unlocked ? status.dataset.unlockedText : status.dataset.lockedText;
          });
          if (this.bar) this.bar.style.width = `${Math.min(qty / (Number(this.dataset.max) || 1), 1) * 100}%`;
        }
      }
    );
  }

  /* ---------- Add-on product rows (bundle offer, upsells) ---------- */
  class OxAddonList extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      this.moneyFormat = this.dataset.moneyFormat;
      this.rows = [...this.querySelectorAll('[data-addon]')];
      this.addEventListener('change', (event) => {
        const row = event.target.closest('[data-addon]');
        if (!row) return;
        if (event.target.matches('[data-addon-variant]')) {
          const option = event.target.selectedOptions[0];
          this.setRowVariant(row, Number(option.value), Number(option.dataset.price), Number(option.dataset.compare), !option.disabled);
        }
        row.classList.toggle('is-checked', !!row.querySelector('[data-addon-check]')?.checked);
        this.render();
      });
      this.unsubscribe = onVariantChange(this.dataset.section, (variant) => {
        this.rows
          .filter((row) => row.hasAttribute('data-main'))
          .forEach((row) => this.setRowVariant(row, variant.id, variant.price, variant.compare_at_price || 0, variant.available));
        this.render();
      });
      this.render();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    setRowVariant(row, id, price, compare, available) {
      row.dataset.variantId = id;
      row.dataset.price = price;
      row.dataset.compare = compare;
      row.classList.toggle('is-unavailable', !available);
      row.querySelector('[data-addon-price]').textContent = formatMoney(price, this.moneyFormat);
      const compareEl = row.querySelector('[data-addon-compare]');
      compareEl.textContent = formatMoney(compare, this.moneyFormat);
      compareEl.hidden = !(compare > price);
    }

    selectedRows() {
      return this.rows.filter((row) => {
        const check = row.querySelector('[data-addon-check]');
        if (!check?.checked || row.classList.contains('is-unavailable')) return false;
        return row.hasAttribute('data-main') || !check.disabled;
      });
    }

    selectedItems() {
      return this.selectedRows().map((row) => ({ id: Number(row.dataset.variantId), quantity: 1 }));
    }

    render() {}
  }

  if (!customElements.get('ox-upsells')) {
    customElements.define(
      'ox-upsells',
      class OxUpsells extends OxAddonList {
        connectedCallback() {
          super.connectedCallback();
          this.collect = () => this.selectedItems();
          getFormHooks(this.form)?.extras.add(this.collect);
        }

        disconnectedCallback() {
          super.disconnectedCallback();
          if (this.form) getFormHooks(this.form).extras.delete(this.collect);
        }
      }
    );
  }

  if (!customElements.get('ox-bundle-offer')) {
    customElements.define(
      'ox-bundle-offer',
      class OxBundleOffer extends OxAddonList {
        connectedCallback() {
          super.connectedCallback();
          this.button = this.querySelector('[data-bundle-add]');
          this.error = this.querySelector('[data-error]');
          this.button?.addEventListener('click', () => this.add());
        }

        render() {
          const rows = this.selectedRows();
          const sum = rows.reduce((total, row) => total + Number(row.dataset.price), 0);
          const compareSum = rows.reduce((total, row) => total + Math.max(Number(row.dataset.compare), Number(row.dataset.price)), 0);
          const pct = Number(this.dataset.discount) || 0;
          const total = pct > 0 && rows.length > 1 ? sum - Math.floor((sum * pct) / 100) : sum;
          const totalEl = this.querySelector('[data-total]');
          const compareEl = this.querySelector('[data-total-compare]');
          if (totalEl) totalEl.textContent = formatMoney(total, this.moneyFormat);
          if (compareEl) {
            compareEl.textContent = formatMoney(compareSum, this.moneyFormat);
            compareEl.hidden = !(compareSum > total);
          }
          if (this.button) this.button.disabled = rows.length === 0;
        }

        async add() {
          const items = this.selectedItems();
          if (!items.length) return;
          const label = this.button.querySelector('[data-label]');
          const original = label.textContent;
          this.button.classList.add('loading');
          this.button.querySelector('.loading__spinner')?.classList.remove('hidden');
          this.error.hidden = true;
          try {
            await addToCart(items);
            label.textContent = this.dataset.added || original;
            setTimeout(() => (label.textContent = original), 2500);
          } catch (error) {
            this.error.textContent = error.message;
            this.error.hidden = false;
          } finally {
            this.button.classList.remove('loading');
            this.button.querySelector('.loading__spinner')?.classList.add('hidden');
          }
        }
      }
    );
  }

  /* ---------- Clickable discount code ---------- */
  if (!customElements.get('ox-discount-code')) {
    customElements.define(
      'ox-discount-code',
      class OxDiscountCode extends HTMLElement {
        connectedCallback() {
          this.label = this.querySelector('[data-label]');
          this.original = this.label?.textContent;
          this.querySelector('[data-copy]')?.addEventListener('click', () => this.activate());
        }

        async copy(code) {
          try {
            await navigator.clipboard.writeText(code);
          } catch (error) {
            const area = document.createElement('textarea');
            area.value = code;
            area.setAttribute('readonly', '');
            area.style.position = 'fixed';
            area.style.opacity = '0';
            document.body.append(area);
            area.select();
            document.execCommand('copy');
            area.remove();
          }
        }

        async apply(code) {
          const cart = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
          const codes = (cart.discount_codes || []).map((d) => d.code);
          if (codes.some((c) => c.toLowerCase() === code.toLowerCase())) return;
          const drawer = document.querySelector('cart-drawer');
          const sections = drawer ? drawer.getSectionsToRender().map((s) => s.id) : [];
          const state = await fetch(`${window.routes.cart_update_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: [...codes, code].join(','), sections, sections_url: window.location.pathname }),
          }).then((r) => r.json());
          if (drawer && state.sections) {
            drawer.getSectionsToRender().forEach((section) => {
              const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
              if (!target || !state.sections[section.id]) return;
              target.innerHTML = drawer.getSectionInnerHTML(state.sections[section.id], section.selector);
            });
            drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
          }
        }

        async activate() {
          const code = this.dataset.code;
          await this.copy(code);
          this.classList.add('is-copied');
          if (this.label) this.label.textContent = this.dataset.copied || this.original;
          if (this.dataset.apply === 'true') {
            try {
              await this.apply(code);
              if (this.label && this.dataset.applied) this.label.textContent = this.dataset.applied;
            } catch (error) {
              console.error(error);
            }
          }
          clearTimeout(this.reset);
          this.reset = setTimeout(() => {
            this.classList.remove('is-copied');
            if (this.label) this.label.textContent = this.original;
          }, 3000);
        }
      }
    );
  }

  /* ---------- Link / scroll-to buttons ---------- */
  if (!customElements.get('ox-scroll-buttons')) {
    customElements.define(
      'ox-scroll-buttons',
      class OxScrollButtons extends HTMLElement {
        connectedCallback() {
          this.addEventListener('click', (event) => {
            const button = event.target.closest('[data-scroll-target]');
            if (!button) return;
            const target = this.resolve(button.dataset.scrollTarget);
            if (!target) return;
            const header = document.querySelector('.section-header.shopify-section-header-sticky, sticky-header');
            const offset = header ? header.getBoundingClientRect().height : 0;
            window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset - 12, behavior: 'smooth' });
          });
        }

        resolve(value) {
          const presets = {
            reviews: ['.ox-review-summary', 'ox-review-summary', '[id^="shopify-section"][id*="review" i]', '[id*="judgeme" i]', '.ox-reviews'],
            faq: ['[id^="shopify-section"][id*="faq" i]', '.ox-product-tabs', '.ox-collapsible', '.product__accordion'],
            description: ['.product__description'],
            top: [`#MainProduct-${this.dataset.section}`],
          };
          const selectors = presets[value] || [value];
          for (const selector of selectors) {
            try {
              const found = document.querySelector(selector);
              if (found) return found;
            } catch (error) {
              return null;
            }
          }
          return null;
        }
      }
    );
  }

  /* ---------- Content tabs ---------- */
  if (!customElements.get('ox-info-tabs')) {
    customElements.define(
      'ox-info-tabs',
      class OxInfoTabs extends HTMLElement {
        connectedCallback() {
          this.tabs = [...this.querySelectorAll('[data-tab]')];
          this.panels = [...this.querySelectorAll('[data-panel]')];
          this.tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => this.select(index));
            tab.addEventListener('keydown', (event) => {
              if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
              const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
              this.select(next);
              this.tabs[next].focus();
            });
          });
          this.select(0);
        }

        select(index) {
          this.tabs.forEach((tab, i) => {
            tab.setAttribute('aria-selected', String(i === index));
            tab.tabIndex = i === index ? 0 : -1;
          });
          this.panels.forEach((panel, i) => (panel.hidden = i !== index));
        }
      }
    );
  }

  /* ---------- Subscriptions (selling plans) ---------- */
  if (!customElements.get('ox-subscriptions')) {
    customElements.define(
      'ox-subscriptions',
      class OxSubscriptions extends HTMLElement {
        connectedCallback() {
          try {
            this.prices = JSON.parse(this.querySelector('[data-plan-prices]').textContent);
          } catch (error) {
            this.prices = {};
          }
          this.addEventListener('change', () => this.markSelected());
          this.unsubscribe = onVariantChange(this.dataset.section, (variant) => {
            const prices = this.prices[variant.id] || {};
            this.querySelectorAll('[data-plan-price]').forEach((el) => {
              const price = prices[el.dataset.planPrice];
              el.textContent = price === undefined ? '' : formatMoney(price, this.dataset.moneyFormat);
            });
          });
          this.markSelected();
        }

        disconnectedCallback() {
          this.unsubscribe?.();
        }

        markSelected() {
          this.querySelectorAll('.ox-subs__option').forEach((option) => {
            option.classList.toggle('is-selected', !!option.querySelector('input')?.checked);
          });
        }
      }
    );
  }

  // Shared with section components (shoppable image, sticky CTA).
  window.oxProduct = { addToCart, formatMoney };
})();
